import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero, Eyebrow } from "@/components/site/ui";
import { Reveal, Stagger, StaggerItem } from "@/components/site/motion";
import { COUNTRIES, EXPANSION_MARKETS } from "@/data/content";

export default function Pays() {
  return (
    <>
      <PageHero
        eyebrow="Découvrez les pays"
        titleLines={["Chiffres clés", "& événements"]}
        subtitle="Une lecture approfondie de nos trois zones d'ancrage : la France & Outre-mer, le Bénin et la Côte d'Ivoire."
      />

      <section className="bg-cream py-24 sm:py-32" data-testid="pays-cards">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid gap-10 md:grid-cols-2 lg:grid-cols-3">
          {COUNTRIES.map((c) => (
            <Reveal key={c.key} delay={0.1}>
              <div className="rounded-3xl overflow-hidden border border-navy/10 bg-white" data-testid={`pays-${c.key}`}>
                <div className="relative h-64 overflow-hidden">
                  <img src={c.image} alt={`${c.name}, ${c.city}`} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/85 to-transparent" />
                  <div className="absolute bottom-5 left-6">
                    <span className="text-xs uppercase tracking-[0.2em] text-gold-light">{c.city} · {c.tag}</span>
                    <p className="font-display text-4xl text-white">{c.flag} {c.name}</p>
                  </div>
                </div>
                <div className="p-8">
                  <p className="text-anthracite font-body leading-relaxed">{c.desc}</p>
                  <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {c.stats.map((s) => (
                      <div key={s.v} className="rounded-xl bg-cream p-3 text-center">
                        <div className="font-display text-xl font-semibold text-gold-gradient">{s.k}</div>
                        <p className="text-[11px] text-anthracite font-body">{s.v}</p>
                      </div>
                    ))}
                  </div>
                  <h4 className="mt-8 font-display text-sm uppercase tracking-[0.2em] text-navy">Événements clés</h4>
                  <div className="mt-4 relative border-l-2 border-gold/30 pl-6 space-y-5">
                    {c.events.map((e) => (
                      <div key={e.year} className="relative">
                        <span className="absolute -left-[29px] top-1 h-3 w-3 rounded-full bg-gold ring-4 ring-white" />
                        <span className="font-display font-bold text-gold">{e.year}</span>
                        <p className="text-sm text-anthracite font-body">{e.label}</p>
                      </div>
                    ))}
                  </div>

                  <h4 className="mt-8 font-display text-sm uppercase tracking-[0.2em] text-navy">Monuments & lieux emblématiques</h4>
                  <div className="mt-4 space-y-4">
                    <figure className="group relative overflow-hidden rounded-2xl">
                      <img src={c.gallery[0].url} alt={`${c.name} — ${c.gallery[0].caption}`} loading="lazy" className="w-full h-72 sm:h-96 object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-navy/20 to-transparent" />
                      <figcaption className="absolute bottom-5 left-5 right-5">
                        <span className="text-[11px] uppercase tracking-[0.2em] text-gold-light">Emblème</span>
                        <p className="font-display text-xl sm:text-2xl text-white">{c.gallery[0].caption}</p>
                      </figcaption>
                    </figure>
                    <div className="grid gap-4 sm:grid-cols-2">
                      {c.gallery.slice(1).map((g, gi) => (
                        <figure key={gi} className="group relative overflow-hidden rounded-2xl">
                          <img src={g.url} alt={`${c.name} — ${g.caption}`} loading="lazy" className="w-full h-52 sm:h-60 object-cover transition-transform duration-[1200ms] group-hover:scale-110" />
                          <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent" />
                          <figcaption className="absolute bottom-4 left-4 right-4 font-display text-white text-sm sm:text-base">{g.caption}</figcaption>
                        </figure>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-night py-20" data-testid="pays-expansion">
        <div className="mx-auto max-w-4xl px-5 sm:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-medium text-white">Un autre pays vous <em className="not-italic italic text-gold-gradient">intéresse</em> ?</h2>
          <p className="mt-4 text-white/70 font-body">{EXPANSION_MARKETS.join(" · ")} — contactez-nous pour explorer de nouveaux horizons.</p>
          <Link to="/contact" className="mt-8 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-light px-7 py-3.5 font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5" data-testid="pays-contact-cta">
            Nous contacter <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </>
  );
}
